import { useEffect, useState } from 'react'

import { Box, Button, Spinner, Text } from '@factorialco/deprecated-design-system'
import {
  CreateRecommendationResultDocument,
  GetRecommendationsEnabledDocument,
  UpdateRecommendationResultDocument,
} from 'generated/resources/graphql'

import TextArea from 'modules/core/components/TextArea'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface RecommendationSystemProps {
  expenseId: number
}

interface RecommendationResult {
  flags: string[]
  relevantPolicies: string[]
}

const RecommendationSystem = ({ expenseId }: RecommendationSystemProps) => {
  const { company } = useCurrentSession()
  const companyId = company?.id
  const [isLoading, setIsLoading] = useState(false)
  const [recommendation, setRecommendation] = useState<RecommendationResult | null>(null)
  const [recommendationId, setRecommendationId] = useState<number | null>(null)
  const [humanComment, setHumanComment] = useState('')
  const [showFeedback, setShowFeedback] = useState(true)
  const [hasStarted, setHasStarted] = useState(false)

  // Query to check if recommendations are enabled
  const { data, loading: queryLoading } = useQuery(GetRecommendationsEnabledDocument)

  // Mutation to create a recommendation result
  const [createRecommendation, { loading: mutationLoading }] = useMutation(
    CreateRecommendationResultDocument,
    {
      extractorFn: (data) => data.expensesMutations.createExpensesRecommendationResult,
    }
  )

  // Mutation to update a recommendation result
  const [updateRecommendation, { loading: updateLoading }] = useMutation(
    UpdateRecommendationResultDocument,
    {
      extractorFn: (data) => data.expensesMutations.updateExpensesRecommendationResult,
      onCompleted: (response) => {
        const result = response?.expensesMutations?.updateExpensesRecommendationResult
        if (result?.recommendationResult) {
          app.setFlash({
            type: 'correct',
            message: 'Feedback submitted',
          })
        }
      },
    }
  )

  const enabled = data?.permissions?.contextlessPolicyResolutions?.[0]?.can ?? false

  const handlePerformRecommendation = async () => {
    if (!companyId) return

    setIsLoading(true)
    setHasStarted(true)
    try {
      const result = await createRecommendation({
        variables: {
          companyId,
          expenseId,
        },
      })

      const handled = handleResult(result, (errors) => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('errors.generic_error'),
          time: 3000,
        })
      })

      if (handled.success) {
        const recommendationResult = handled.data?.recommendationResult
        if (recommendationResult?.aiResponse) {
          const { flags, relevantPolicies } = recommendationResult.aiResponse
          setRecommendation({
            flags: flags || [],
            relevantPolicies: relevantPolicies || [],
          })
          setRecommendationId(recommendationResult.id)
        }
      }
    } finally {
      setIsLoading(false)
    }
  }

  // Automatically trigger recommendation on mount
  useEffect(() => {
    if (enabled && companyId && !hasStarted && !recommendation) {
      handlePerformRecommendation()
    }
  }, [enabled, companyId, hasStarted, recommendation])

  const handleFeedback = async (validation: 'good' | 'bad') => {
    if (!recommendationId) return

    // Track feedback event
    if (validation === 'good') {
      track(trackingEvents.expenses.aiPolicyCheckFeedbackHelpful)
    } else {
      track(trackingEvents.expenses.aiPolicyCheckFeedbackNotHelpful)
    }

    try {
      const result = await updateRecommendation({
        variables: {
          id: recommendationId,
          humanValidation: validation,
          humanComment: humanComment || null,
        },
      })

      const handled = handleResult(result, (errors) => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('errors.generic_error'),
          time: 3000,
        })
      })

      if (handled.success) {
        setShowFeedback(false)
      }
    } catch (error) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_error'),
        time: 3000,
      })
    }
  }

  // Don't render if loading query or not enabled
  if (queryLoading || !enabled) {
    return null
  }

  const isProcessing = isLoading || mutationLoading
  const isFeedbackProcessing = updateLoading

  return (
    <Box
      padding="s16"
      gap="s16"
      background="neutralBackgroundSubtle"
      borderRadius={{ all: 'abs008' }}
      style={{ border: '1px solid #E5E7EB' }}
    >
      {/* Header Section */}
      <Box gap="s4">
        <Text.Normal weight="bold" color="grey1000">
          AI Insights (beta)
        </Text.Normal>
        <Text.Normal color="grey700">
          An AI summary of potential policy flags for your review. This is advisory; please make the
          final call.
        </Text.Normal>
      </Box>

      {/* Loading State */}
      {isProcessing && !recommendation && (
        <Box
          width="full"
          justifyContent="center"
          alignItems="center"
          padding="s16"
          gap="s8"
          background="white"
          borderRadius={{ all: 'abs008' }}
        >
          <Box style={{ width: '16px', height: '16px' }}>
            <Spinner size="s16" />
          </Box>
          <Text.Normal color="grey700">Analyzing expense against policy...</Text.Normal>
        </Box>
      )}

      {/* Recommendation Result */}
      {recommendation && (
        <Box width="full" gap="s12">
          <Box
            padding="s16"
            background="white"
            borderRadius={{ all: 'abs008' }}
            gap="s16"
            style={{ border: '1px solid #E5E7EB' }}
          >
            <Box gap="s12">
              <Text.Normal color="grey700" weight="medium">
                What we noticed
              </Text.Normal>
              {recommendation.flags.length === 0 ||
              (recommendation.flags.length === 1 &&
                recommendation.flags[0]?.toLowerCase().includes('no flags')) ? (
                <Text.Normal color="grey1000">
                  No flags detected - expense appears compliant
                </Text.Normal>
              ) : (
                <Box gap="s4">
                  {recommendation.flags.map((flag, index) => (
                    <Text.Normal key={index} color="grey1000">
                      • {flag}
                    </Text.Normal>
                  ))}
                </Box>
              )}
            </Box>
            {recommendation.relevantPolicies.length > 0 && (
              <Box gap="s8">
                <Text.Normal color="grey700" weight="medium">
                  Relevant policies
                </Text.Normal>
                <Box gap="s8">
                  {recommendation.relevantPolicies.map((policy, index) => (
                    <Text.Normal key={index} color="grey1000">
                      • {policy}
                    </Text.Normal>
                  ))}
                </Box>
              </Box>
            )}
          </Box>

          {/* Feedback Section */}
          {showFeedback && (
            <Box
              width="full"
              gap="s12"
              padding="s16"
              background="white"
              borderRadius={{ all: 'abs008' }}
              style={{ border: '1px solid #E5E7EB' }}
            >
              <Text.Normal color="grey700" weight="medium">
                WAS THIS HELPFUL?
              </Text.Normal>
              <TextArea
                value={humanComment}
                onChange={(value) => setHumanComment(value)}
                placeholder="Optional: Add feedback to help improve recommendations"
              />
              <Box flexDirection="row" gap="s8" justifyContent="flexEnd">
                <Button
                  type="secondary"
                  onClick={() => handleFeedback('bad')}
                  disabled={isFeedbackProcessing}
                  loading={isFeedbackProcessing}
                  iconName="thumbs-down"
                >
                  Not helpful
                </Button>
                <Button
                  type="primary"
                  onClick={() => handleFeedback('good')}
                  disabled={isFeedbackProcessing}
                  loading={isFeedbackProcessing}
                  iconName="thumbs-up"
                >
                  Helpful
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}

export default RecommendationSystem
